import { useEffect, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from 'components';
import { AmbossEmbed } from './AmbossEmbed';
import { AmbossHeader } from './AmbossHeader';
import { AmbossHelpOverlay } from './AmbossHelpOverlay';
import { AmbossHistoryView } from './AmbossHistoryView';
import { AmbossSearchPanel } from './AmbossSearchPanel';
import {
  AMBOSS_CONTENT_BOTTOM,
  AmbossCard,
  AmbossCardBody,
  AmbossEmbedFrame,
  AmbossHelpPanel,
  AmbossHistoryOverlay,
  AmbossIconButton,
  AmbossScrim,
  AmbossSearchCard,
} from './amboss.styles';
import {
  getAmbossDataGatePhase,
  retryAmbossDataGate,
  subscribeToAmbossDataGate,
} from './ambossDataGate';
import { useAmboss } from './useAmboss';

const OFFSCREEN = 'translateY(200vh)';

export function AmbossLayer({ topOffsetPx }: { topOffsetPx: number }) {
  const { t } = useTranslation();
  const { screen, overlay, embedUrl, submissionId, reportCid, close } = useAmboss();
  const dataGatePhase = useSyncExternalStore(subscribeToAmbossDataGate, getAmbossDataGatePhase);

  const isOpen = screen !== 'closed';

  useEffect(() => {
    if (!isOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) close();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [isOpen, close]);

  const hasHeader = screen === 'embedded' || overlay !== 'none';
  const scrim = isOpen && <AmbossScrim />;
  const cardBounds = { top: topOffsetPx, bottom: AMBOSS_CONTENT_BOTTOM };

  if (dataGatePhase !== 'ready') {
    if (!isOpen) return null;
    const blocked = dataGatePhase === 'blocked' && (
      <div className="flex h-full w-full flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-sm text-foreground">{t('amboss.embed.error')}</p>
        <Button onClick={retryAmbossDataGate}>{t('amboss.embed.retry')}</Button>
      </div>
    );
    return (
      <>
        {scrim}
        {hasHeader ? (
          <AmbossCard style={cardBounds}>
            <AmbossHeader />
            <AmbossCardBody>{blocked}</AmbossCardBody>
          </AmbossCard>
        ) : (
          <AmbossSearchCard style={{ top: topOffsetPx }}>
            <div className="flex justify-end px-3 pt-2">
              <AmbossIconButton aria-label={t('amboss.actions.close')} onClick={close}>
                <X size={20} />
              </AmbossIconButton>
            </div>
            {blocked}
          </AmbossSearchCard>
        )}
      </>
    );
  }

  const showCard = isOpen && hasHeader;

  return (
    <>
      {scrim}
      {(embedUrl || showCard) && (
        // Kept mounted while hidden and moved offscreen instead of CSS-hidden:
        // hiding an Electron <webview> can leak or reload its guest. The inert
        // card ignores input while offscreen.
        <AmbossCard
          style={{ ...cardBounds, transform: showCard ? undefined : OFFSCREEN }}
          inert={!showCard}
        >
          {showCard && <AmbossHeader />}
          <AmbossCardBody>
            {embedUrl && (
              <AmbossEmbedFrame
                style={{ transform: screen !== 'embedded' ? OFFSCREEN : undefined }}
                inert={screen !== 'embedded'}
              >
                <AmbossEmbed url={embedUrl} reloadToken={submissionId} onCid={reportCid} />
              </AmbossEmbedFrame>
            )}
            {overlay === 'help' && (
              <AmbossHelpPanel>
                <AmbossHelpOverlay />
              </AmbossHelpPanel>
            )}
            {overlay === 'history' && (
              <AmbossHistoryOverlay>
                <AmbossHistoryView />
              </AmbossHistoryOverlay>
            )}
          </AmbossCardBody>
        </AmbossCard>
      )}
      {screen === 'search' && overlay === 'none' && (
        <AmbossSearchCard style={{ top: topOffsetPx }}>
          <AmbossSearchPanel />
        </AmbossSearchCard>
      )}
    </>
  );
}
